<template>
  <div>
    <template v-for="group in groups">
      <div :key="`g-${group.key || group.entity}`" class="sp-grp">
        <v-icon x-small>{{ group.icon }}</v-icon>
        {{ group.label }} · {{ group.total }}
        <button v-if="group.collapsible" :aria-expanded="String(!group.collapsed)" class="sp-grp__more" type="button"
                @click="$emit('toggle-group', group.key || group.entity)">
          {{ group.collapsed ? 'anzeigen' : 'ausblenden' }}
        </button>
        <button v-if="group.canExpand" :disabled="group.expanding" class="sp-grp__more" type="button" @click="$emit('expand', group.entity)">
          {{ group.expanding ? 'lädt …' : 'alle anzeigen' }}
        </button>
      </div>
      <div v-if="group.hint && !group.collapsed" :key="`h-${group.key || group.entity}`" class="sp-grp__hint">{{ group.hint }}</div>
      <template v-for="hit in (group.collapsed ? [] : group.items)">
        <a :key="keyOf(hit)" :class="{ 'sp-hit--active': keyOf(hit) === activeKey, 'sp-hit--inactive': hit.isActive === false }"
           :data-key="keyOf(hit)" :href="hrefOf(hit)" class="sp-hit" role="option" :aria-selected="String(keyOf(hit) === activeKey)"
           @click.prevent="onClick(hit, $event)" @mouseenter="$emit('hover', hit)">
          <span class="sp-hit__ini">{{ initials(hit.title) }}</span>
          <span class="sp-hit__main">
            <span class="sp-hit__title d-block">
              <template v-for="(part, i) in highlight(hit.title)">
                <mark v-if="part.mark" :key="i">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
            <span v-if="hit.customId || hit.subtitle || whyOf(hit)" class="sp-hit__sub d-block">
              <template v-for="(part, i) in highlight(subOf(hit))">
                <mark v-if="part.mark" :key="i">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
              <span v-if="whyOf(hit)" class="sp-hit__why"><template v-if="subOf(hit)"> · </template>gefunden über {{ whyOf(hit) }}</span>
            </span>
          </span>
          <span class="sp-hit__end">
            <!-- A deactivated profile only shows "inaktiv"; its registration status (e.g. "Aktiv") would contradict it -->
            <span v-if="hit.status && hit.isActive !== false" :class="toneOf(hit)" class="tag tag--sm">{{ hit.status }}</span>
            <span v-if="hit.isActive === false" class="tag tag--sm tone-muted">inaktiv</span>
            <v-btn v-if="mobile" v-tip="compactKey === keyOf(hit) ? 'Schnellansicht schließen' : 'Schnellansicht'" :aria-label="compactKey === keyOf(hit) ? 'Schnellansicht schließen' : 'Schnellansicht'" icon x-small
                   @click.prevent.stop="$emit('toggle-compact', hit)">
              <v-icon small>{{ compactKey === keyOf(hit) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </span>
        </a>
        <search-quick-view v-if="mobile && compactKey === keyOf(hit)" :key="`qv-${keyOf(hit)}`" :hit="hit" compact
                           @navigate="(to, newTab) => $emit('navigate', to, newTab)" @open="$emit('open', {hit: $event, newTab: false})"
                           @open-tab="$emit('open', {hit: $event, newTab: true})"/>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import Vue, {PropType} from 'vue';
import {
  highlightSegments,
  initialsOf,
  matchedOnText,
  PaletteEntity,
  PaletteHit,
  routeOfEntity,
  statusTone,
  TextSegment,
} from '@/components/app/search/searchEntities';
import SearchQuickView from '@/components/app/search/searchQuickView.vue';

export type ResultGroup = {
  entity: PaletteEntity;
  label: string;
  icon: string;
  total: number;
  items: PaletteHit[];
  canExpand: boolean;
  expanding: boolean;
  /** Unique key if several groups share an entity (e.g. "Weitere Schulen (Akquise)") */
  key?: string;
  /** Group can be collapsed; collapsed groups render no hits (and are skipped by the keyboard navigation) */
  collapsible?: boolean;
  collapsed?: boolean;
  /** Short explanation below the header */
  hint?: string;
};

/** Hits grouped by area with highlighted term, status tag and "gefunden über …" */
export default Vue.extend({
  components: {SearchQuickView},
  props: {
    groups: {type: Array as PropType<ResultGroup[]>, required: true},
    term: {type: String, default: ''},
    activeKey: {type: String, default: ''},
    /** Hit whose compact quick view is expanded (mobile) */
    compactKey: {type: String, default: ''},
    mobile: {type: Boolean, default: false},
  },
  methods: {
    keyOf(hit: PaletteHit): string {
      return `${hit.entity}:${hit.id}`;
    },
    hrefOf(hit: PaletteHit): string | undefined {
      const route = routeOfEntity(hit.entity, hit.id);
      return route ? this.$router.resolve(route).href : undefined;
    },
    initials(title: string): string {
      return initialsOf(title);
    },
    highlight(text: string): TextSegment[] {
      return highlightSegments(text, this.term);
    },
    subOf(hit: PaletteHit): string {
      return [hit.customId, hit.subtitle].filter((el) => el && el !== hit.title).join(' · ');
    },
    whyOf(hit: PaletteHit): string {
      return matchedOnText(hit);
    },
    /** Tone of the status tag; inactivity has its own grey tag */
    toneOf(hit: PaletteHit): string {
      return statusTone({entity: hit.entity});
    },
    onClick(hit: PaletteHit, event: MouseEvent) {
      this.$emit('open', {hit, newTab: event.metaKey || event.ctrlKey});
    },
  },
});
</script>
